import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { GradientText, CountingNumber } from '@/components/animate-ui'
import AnimatedStatCard from '@/components/enhanced/animated-stat-card'
import BGVStatusWidget from '@/components/social/BGVStatusWidget'
import { useApprovalCounts } from '@/hooks/useApprovalCounts'
import { 
  Users, 
  MessageCircle, 
  Image, 
  TrendingUp, 
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  Building2,
  Camera,
  Star,
  BarChart3,
  Filter,
  CheckSquare
} from 'lucide-react'

const statsCards = [
  {
    title: 'Total Leads',
    value: 247,
    change: '+12%',
    trend: 'up' as const,
    icon: MessageCircle,
    description: 'from last month',
    isNumber: true
  },
  {
    title: 'Active Galleries',
    value: 89,
    change: '+5%',
    trend: 'up' as const,
    icon: Camera,
    description: 'published galleries',
    isNumber: true
  },
  {
    title: 'Pending Approvals',
    value: 12,
    change: '-3%',
    trend: 'down' as const,
    icon: Clock,
    description: 'awaiting review',
    isNumber: true
  },
  {
    title: 'Customer Reviews',
    value: 4.8,
    change: '+0.2',
    trend: 'up' as const,
    icon: Star,
    description: 'average rating',
    isNumber: true,
    decimals: 1
  }
]

const recentActivity = [
  {
    type: 'lead',
    title: 'New lead from Toronto',
    description: 'Brick repair inquiry - 123 Main St',
    time: '5 minutes ago',
    icon: MessageCircle,
    status: 'new'
  },
  {
    type: 'gallery',
    title: 'Gallery approved',
    description: 'Tuckpointing project in Hamilton',
    time: '1 hour ago',
    icon: CheckCircle2,
    status: 'success'
  },
  {
    type: 'review',
    title: 'New 5-star review',
    description: 'Excellent chimney repair work',
    time: '2 hours ago',
    icon: Star,
    status: 'success'
  },
  {
    type: 'social',
    title: 'Instagram post scheduled',
    description: 'Before/after gallery content',
    time: '3 hours ago',
    icon: Calendar,
    status: 'scheduled'
  },
  {
    type: 'alert',
    title: 'WSIB certificate expiring',
    description: 'Expires in 30 days',
    time: '1 day ago',
    icon: AlertCircle,
    status: 'warning'
  }
]

const quickActions = [
  {
    title: 'Create New Gallery',
    description: 'Upload and organize project photos',
    icon: Camera,
    href: '/media/galleries',
    color: 'bg-blue-500'
  },
  {
    title: 'Review Pending Items',
    description: 'Approve galleries and images',
    icon: CheckSquare,
    href: '/media/approval',
    color: 'bg-amber-500',
    hasBadge: true
  },
  {
    title: 'Review Leads',
    description: 'Process customer inquiries',
    icon: MessageCircle,
    href: '/customers/leads',
    color: 'bg-purple-500'
  },
  {
    title: 'Schedule Social Post',
    description: 'Create social media content',
    icon: Calendar,
    href: '/marketing/social/create',
    color: 'bg-orange-500'
  }
]

// Additional approval-specific quick actions
const approvalActions = [
  {
    title: 'Gallery Approvals',
    description: 'Review pending galleries',
    icon: Camera,
    href: '/media/approval?type=galleries',
    color: 'bg-indigo-500'
  },
  {
    title: 'Image Approvals',
    description: 'Review pending images',
    icon: Image,
    href: '/media/approval?type=images',
    color: 'bg-cyan-500'
  },
  {
    title: 'Filter by Region',
    description: 'Review by franchise area',
    icon: Filter,
    href: '/media/approval',
    color: 'bg-emerald-500'
  }
]

export default function Dashboard() {
  const { data: approvalCounts } = useApprovalCounts()

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div 
        className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            <GradientText 
              gradient="from-primary via-primary/90 to-primary/70"
              animate={false}
            >
              Dashboard
            </GradientText>
          </h1>
          <motion.p 
            className="text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Welcome back! Here's what's happening with your business.
          </motion.p>
        </div>
        <motion.div 
          className="flex gap-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Button>
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Report
          </Button>
          <Button variant="outline">
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </Button>
        </motion.div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => (
          <AnimatedStatCard
            key={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            trend={stat.trend}
            icon={stat.icon}
            description={stat.description}
            delay={index * 0.1}
            isNumber={stat.isNumber}
            decimals={stat.decimals || 0}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Recent Activity */}
        <motion.div
          className="md:col-span-1 lg:col-span-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>
                Latest updates from across your business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-4 p-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 + (index * 0.1) }}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div 
                      className={`p-2 rounded-lg ${
                        activity.status === 'success' ? 'bg-green-100 text-green-600' :
                        activity.status === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                        activity.status === 'scheduled' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-600'
                      }`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <activity.icon className="h-4 w-4" />
                    </motion.div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* BGV Integration Status */}
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <BGVStatusWidget />

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common tasks and shortcuts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {quickActions.map((action, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 + (index * 0.1) }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-start h-auto p-4 hover:bg-accent/50 transition-colors"
                      asChild
                    >
                      <Link to={action.href}>
                        <motion.div 
                          className={`p-2 rounded-lg mr-3 ${action.color} text-white`}
                          whileHover={{ rotate: 5, scale: 1.1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <action.icon className="h-4 w-4" />
                        </motion.div>
                        <div className="text-left flex-1">
                          <div className="font-medium">{action.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {action.description}
                          </div>
                        </div>
                        {action.hasBadge && approvalCounts?.totalPending && approvalCounts.totalPending > 0 && (
                          <Badge className="ml-2 bg-red-500 text-white">
                            {approvalCounts.totalPending}
                          </Badge>
                        )}
                      </Link>
                    </Button>
                  </motion.div>
                ))}
              </div>

              {/* Approval-specific quick actions */}
              {approvalCounts?.totalPending && approvalCounts.totalPending > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                    Approval Shortcuts
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {approvalActions.map((action, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 1.1 + (index * 0.1) }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start h-auto p-3"
                          asChild
                        >
                          <Link to={action.href}>
                            <motion.div 
                              className={`p-1.5 rounded mr-2 ${action.color} text-white`}
                              whileHover={{ rotate: 3, scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <action.icon className="h-3 w-3" />
                            </motion.div>
                            <div className="text-left">
                              <div className="text-sm font-medium">{action.title}</div>
                              <div className="text-xs text-muted-foreground">
                                {action.description}
                              </div>
                            </div>
                          </Link>
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Performance Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Performance Overview</CardTitle>
            <CardDescription>
              Key metrics and trends for your business
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <motion.div 
                className="space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.9 }}
              >
                <p className="text-sm font-medium">Lead Conversion Rate</p>
                <p className="text-2xl font-bold">
                  <CountingNumber value={23.5} decimals={1} suffix="%" />
                </p>
                <p className="text-xs text-muted-foreground">
                  <motion.span 
                    className="text-green-600"
                    whileHover={{ scale: 1.05 }}
                  >
                    +2.1%
                  </motion.span> from last month
                </p>
              </motion.div>
              <motion.div 
                className="space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1.0 }}
              >
                <p className="text-sm font-medium">Gallery Engagement</p>
                <p className="text-2xl font-bold">
                  <CountingNumber value={85} suffix="%" />
                </p>
                <p className="text-xs text-muted-foreground">
                  <motion.span 
                    className="text-green-600"
                    whileHover={{ scale: 1.05 }}
                  >
                    +5.2%
                  </motion.span> from last month
                </p>
              </motion.div>
              <motion.div 
                className="space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1.1 }}
              >
                <p className="text-sm font-medium">Social Media Reach</p>
                <p className="text-2xl font-bold">
                  <CountingNumber value={12.3} decimals={1} suffix="k" />
                </p>
                <p className="text-xs text-muted-foreground">
                  <motion.span 
                    className="text-green-600"
                    whileHover={{ scale: 1.05 }}
                  >
                    +8.7%
                  </motion.span> from last month
                </p>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}